import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, ArrowLeft, Eye, EyeOff, KeyRound } from 'lucide-react';
import { traderAuthAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../lib/analytics';
import { cabinetPath } from '../lib/domains';

interface TraderAuthPageProps {
    mode: 'login' | 'register';
}

export const TraderAuthPage: React.FC<TraderAuthPageProps> = ({ mode }) => {
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resetMessage, setResetMessage] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const isLogin = mode === 'login';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            if (isLogin) {
                const response = await traderAuthAPI.login({
                    username: formData.username,
                    password: formData.password
                });
                localStorage.setItem('trader_access_token', response.access_token);
                // Обновляем контекст и переходим
                await refreshUser();
                trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SUCCEEDED, {
                    user_type: 'trader',
                    auth_mode: 'login',
                });
                navigate(cabinetPath('trader', '/dashboard'));
            } else {
                // Регистрация
                if (formData.password !== formData.confirmPassword) {
                    setError('Пароли не совпадают');
                    return;
                }

                await traderAuthAPI.register({
                    username: formData.username,
                    email: formData.email,
                    password: formData.password
                });

                // После успешной регистрации сразу логинимся
                const response = await traderAuthAPI.login({
                    username: formData.username,
                    password: formData.password
                });
                localStorage.setItem('trader_access_token', response.access_token);
                // Обновляем контекст и переходим
                await refreshUser();
                trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SUCCEEDED, {
                    user_type: 'trader',
                    auth_mode: 'register',
                });
                navigate(cabinetPath('trader', '/dashboard'));
            }
        } catch (err: any) {
            const message = err.response?.data?.detail || 'Произошла ошибка';
            trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_FAILED, {
                user_type: 'trader',
                auth_mode: isLogin ? 'login' : 'register',
                error_message: message,
            });
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (error) setError(null);
        if (resetMessage) setResetMessage(null);
    };

    const handlePasswordResetRequest = async () => {
        const identifier = formData.username.trim();
        setError(null);
        setResetMessage(null);

        if (!identifier) {
            setError('Введите имя пользователя или email');
            return;
        }

        setResetLoading(true);
        try {
            const response = await traderAuthAPI.requestPasswordReset(identifier);
            setResetMessage(response.message);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Не удалось отправить запрос');
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <Link to="/" className="inline-flex items-center text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 mb-4">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Назад к выбору
                    </Link>
                    <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-950 rounded-full flex items-center justify-center mb-4">
                        <Users className="h-8 w-8 text-blue-600 dark:text-blue-300" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100">
                        {isLogin ? 'Вход для трейдеров' : 'Регистрация трейдера'}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">
                        {isLogin
                            ? 'Войдите в свой аккаунт трейдера'
                            : 'Создайте аккаунт трейдера для работы с платежами'
                        }
                    </p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>
                            {isLogin ? 'Авторизация' : 'Создание аккаунта'}
                        </CardTitle>
                        <CardDescription>
                            {isLogin
                                ? 'Введите свои учетные данные'
                                : 'Заполните информацию для регистрации'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                                    {error}
                                </div>
                            )}
                            {resetMessage && (
                                <div className="p-3 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                                    {resetMessage}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="username">Имя пользователя</Label>
                                <Input
                                    id="username"
                                    type="text"
                                    value={formData.username}
                                    onChange={(e) => handleInputChange('username', e.target.value)}
                                    placeholder="Введите имя пользователя"
                                    required
                                    disabled={loading}
                                />
                            </div>

                            {!isLogin && (
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        placeholder="Введите email"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="password">Пароль</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        placeholder="Введите пароль"
                                        required
                                        disabled={loading}
                                        minLength={6}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            {!isLogin && (
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
                                    <div className="relative">
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.confirmPassword}
                                            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                            placeholder="Повторите пароль"
                                            required
                                            disabled={loading}
                                            minLength={6}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <Button
                                type="submit"
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                disabled={loading || resetLoading}
                            >
                                {loading ? 'Обработка...' : (isLogin ? 'Войти' : 'Зарегистрироваться')}
                            </Button>
                            {isLogin && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="w-full text-blue-700 dark:text-blue-300"
                                    onClick={handlePasswordResetRequest}
                                    disabled={loading || resetLoading}
                                >
                                    <KeyRound className="h-4 w-4 mr-2" />
                                    {resetLoading ? 'Отправка...' : 'Забыли пароль?'}
                                </Button>
                            )}
                        </form>

                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
